/**
 * Forms opinión · ficha de opinión vía Google Forms
 * (`/api/configuraciones-instrumento`).
 *
 * Ciclo borrador → publicada → cerrada. Al publicar se genera un formulario POR
 * grupo de interés y se congela `preguntas_snapshot`: editar una pregunta
 * después NO altera el formulario ya publicado.
 *
 * Filtrar por `id_instrumento` no es opcional en la práctica: sin ese filtro la
 * ficha recibía todas las configuraciones del sistema y abría la de otro
 * instrumento cualquiera.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http } from '@/services/http'
import type { HttpResult } from '@/services/http'
import type ConfiguracionInstrumento from '@/interfaces/ConfiguracionInstrumento'
import type {
  PreviewConfiguracion,
  RespuestasFormulario,
  DestinatariosPayload,
  CalificarPayload,
  FichaOpinionResultado,
  Participacion,
} from '@/interfaces/ConfiguracionInstrumento'
import type EvaluacionIndicador from '@/interfaces/EvaluacionIndicador'

const BASE = '/api/configuraciones-instrumento'

/**
 * Crea configuracion instrumento · petición `POST`.
 *
 * @returns HttpResult<ConfiguracionInstrumento>
 */
export async function crearConfiguracionService(payload: {
  id_instrumento: number
  /** Opcional: la configuración es una por formulario, no por indicador. */
  id_indicador?: number | null
  vigencia_desde?: string
  vigencia_hasta?: string
  titulo?: string
  instrucciones?: string
}): Promise<HttpResult<ConfiguracionInstrumento>> {
  return http.post<ConfiguracionInstrumento>(BASE, payload)
}

/**
 * Lista configuracion instrumento · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns ConfiguracionInstrumento[]
 */
export async function listarConfiguracionesService(params: {
  id_indicador?: number
  id_instrumento?: number
  estado?: 'borrador' | 'publicada' | 'cerrada'
}): Promise<ConfiguracionInstrumento[]> {
  const query = new URLSearchParams()
  if (params.id_indicador) query.set('id_indicador', String(params.id_indicador))
  if (params.id_instrumento) query.set('id_instrumento', String(params.id_instrumento))
  if (params.estado) query.set('estado', params.estado)
  const url = query.toString() ? `${BASE}?${query}` : BASE
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : (d?.data ?? [])
}

/**
 * La ejecución y el calificador acotan la calificación que trae cada indicador
 * cubierto; sin ellos se devuelve la más reciente registrada.
 */
export async function obtenerConfiguracionService(
  id: number,
  params: { id_ejecucion?: number; id_usuario?: number } = {}
): Promise<HttpResult<ConfiguracionInstrumento>> {
  const query = new URLSearchParams()
  if (params.id_ejecucion) query.set('id_ejecucion', String(params.id_ejecucion))
  if (params.id_usuario) query.set('id_usuario', String(params.id_usuario))
  const url = query.toString() ? `${BASE}/${id}?${query}` : `${BASE}/${id}`
  return http.get<ConfiguracionInstrumento>(url)
}

/**
 * Resultado de las fichas de opinión que evalúan un indicador: la opinión no
 * deja fichas de evidencia (eso es del carril documental), vive en la
 * configuración del formulario.
 */
export async function fichasOpinionDeIndicadorService(
  idIndicador: number,
  params: { id_ejecucion?: number; id_usuario?: number } = {}
): Promise<FichaOpinionResultado[]> {
  const query = new URLSearchParams()
  if (params.id_ejecucion) query.set('id_ejecucion', String(params.id_ejecucion))
  if (params.id_usuario) query.set('id_usuario', String(params.id_usuario))
  const url = `/api/indicadores/${idIndicador}/fichas-opinion${query.toString() ? `?${query}` : ''}`

  const result = await http.get<FichaOpinionResultado[]>(url)
  return result.ok && Array.isArray(result.data) ? result.data : []
}

/**
 * Operación sobre configuracion instrumento · petición `GET`.
 *
 * @returns HttpResult<PreviewConfiguracion>
 */
export async function previewConfiguracionService(id: number): Promise<HttpResult<PreviewConfiguracion>> {
  return http.get<PreviewConfiguracion>(`${BASE}/${id}/preview`)
}

/**
 * Publica configuracion instrumento · petición `POST`.
 *
 * @returns HttpResult<ConfiguracionInstrumento>
 */
export async function publicarConfiguracionService(id: number): Promise<HttpResult<ConfiguracionInstrumento>> {
  return http.post<ConfiguracionInstrumento>(`${BASE}/${id}/publicar`, {})
}

/**
 * La ejecución viaja para archivar los correos bajo SU área: es la que dice a
 * qué gente se le envió, y con ella se ofrecen después como banco reutilizable.
 */
export async function asignarDestinatariosService(
  id: number,
  destinatarios: DestinatariosPayload,
  idEjecucion?: number | null
): Promise<HttpResult<{ message: string; enviados_por_formulario: Record<string, number>; contactos_registrados?: number }>> {
  return http.post(`${BASE}/${id}/asignar`, {
    destinatarios,
    ...(idEjecucion ? { id_ejecucion: idEjecucion } : {})
  })
}

/**
 * Sincroniza configuracion instrumento · petición `POST`.
 *
 * @returns HttpResult<{ message: string; respuestas_nuevas: number }>
 */
export async function sincronizarService(
  id: number
): Promise<HttpResult<{ message: string; respuestas_nuevas: number; participacion?: Participacion }>> {
  return http.post(`${BASE}/${id}/sincronizar`, {})
}

/**
 * Obtiene configuracion instrumento · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns RespuestasFormulario[]
 */
export async function obtenerRespuestasService(id: number): Promise<RespuestasFormulario[]> {
  const result = await http.get<RespuestasFormulario[]>(`${BASE}/${id}/respuestas`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Califica configuracion instrumento · petición `POST`.
 *
 * @returns HttpResult<EvaluacionIndicador>
 */
export async function calificarService(
  id: number,
  payload: CalificarPayload
): Promise<HttpResult<EvaluacionIndicador>> {
  return http.post<EvaluacionIndicador>(`${BASE}/${id}/calificar`, payload)
}

/**
 * Cierra configuracion instrumento · petición `POST`.
 *
 * @returns HttpResult<ConfiguracionInstrumento>
 */
export async function cerrarConfiguracionService(id: number): Promise<HttpResult<ConfiguracionInstrumento>> {
  return http.post<ConfiguracionInstrumento>(`${BASE}/${id}/cerrar`, {})
}
